import '@alphadoc/alphadoc-gantt/styles';
import '@/assets/styles/index.scss';
import { createApp } from 'vue';
import App from '@/App.vue';
import * as Sentry from '@sentry/vue';
import Exception from '@/modules/core/utils/exception.ts';
import hotkeys from 'hotkeys-js';
import i18n from '@/i18n.ts';
import pinia from '@/pinia.ts';
import router from '@/router.ts';
import tanstackQueryOptions, { queryClient } from '@/tanstack-query.ts';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { Settings } from 'luxon';
import vPrefix from '@/modules/common/directives/prefix.ts';
import vPostfix from '@/modules/common/directives/postfix.ts';
import vRotate from '@/modules/common/directives/rotate.ts';
import vFocus from '@/modules/common/directives/focus.ts';
import { setupAuthSessionSync } from '@/modules/core/composables/auth-session-sync.ts';
import { setupDocumentUpdateSync } from '@/modules/shared/composables/document-update-sync.ts';
import { setupCounterpartyUpdateSync } from '@/modules/counterparty/composables/counterparty-update-sync.ts';
import { usePreviewStore } from '@/modules/core/stores/preview.store.ts';
import { useDocumentEditStore } from '@/modules/document/document-edit/stores/document-edit.store.ts';
import { useCounterpartyEditStore } from '@/modules/counterparty/counterparty-edit/stores/counterparty-edit.store.ts';
import { getLastSelectedId } from '@/modules/viewer/composables/use-viewer-selection.ts';
import { setupTextSelectionGuard } from '@/modules/ui/utils/text-selection-guard.ts';
import { ClientPreview, DocumentPreview } from '@/modules/shared/feature-bridges.ts';

const app = createApp(App);

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({ app, dsn: import.meta.env.VITE_SENTRY_DSN });
}

if (import.meta.env.PROD) {
  Exception.init(app);
}

Settings.defaultLocale = i18n.global.locale.value;
hotkeys.filter = () => true;

app.config.globalProperties.$maybeT = import.meta.env.PROD ? i18n.global.t : i18n.global.maybeT;
app.config.globalProperties.$o = i18n.global.o;

app
  .use(i18n)
  .use(pinia)
  .use(router)
  .use(VueQueryPlugin, tanstackQueryOptions)
  .directive('prefix', vPrefix)
  .directive('postfix', vPostfix)
  .directive('rotate', vRotate)
  .directive('focus', vFocus);

setupAuthSessionSync();
setupTextSelectionGuard();

const previewStore = usePreviewStore();
const documentEditStore = useDocumentEditStore();
const counterpartyEditStore = useCounterpartyEditStore();

function getActivePreviewContext(entityId: number, isTargetPreview: boolean, isEditing: boolean) {
  if (previewStore.entityIdPreview !== entityId || !isTargetPreview || isEditing) {
    return null;
  }

  const scope = router.currentRoute.value.name?.toString() ?? null;

  return {
    scope,
    rowId: scope ? getLastSelectedId(scope) : null,
    refresh: () => previewStore.refreshPreviewAction(),
  };
}

setupDocumentUpdateSync(queryClient, {
  getActiveDocumentContext(documentId) {
    return getActivePreviewContext(
      documentId,
      previewStore.interfacePreview instanceof DocumentPreview,
      documentEditStore.isEditingDocument(documentId)
    );
  },
});

setupCounterpartyUpdateSync(queryClient, {
  getActiveCounterpartyContext(counterpartyId) {
    return getActivePreviewContext(
      counterpartyId,
      previewStore.interfacePreview instanceof ClientPreview,
      counterpartyEditStore.isEditingCounterparty(counterpartyId)
    );
  },
});

app.mount('#app');
